import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Eye, EyeOff, Bell, MessageSquare, Layout, Calendar } from 'lucide-react';

const PAGES = [
  { value: 'all', label: 'All Pages' },
  { value: 'homepage', label: 'Homepage' },
  { value: 'catalog', label: 'Course Catalog' },
  { value: 'about', label: 'About' },
  { value: 'blog', label: 'Blog' },
  { value: 'locations', label: 'Locations' },
  { value: 'programs', label: 'Programs' },
  { value: 'summer-camps', label: 'Summer Camps' },
  { value: 'coding', label: 'Coding' },
  { value: 'robotics', label: 'Robotics' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'math', label: 'Math' },
  { value: 'science', label: 'Science' },
];

const TYPE_CONFIG = {
  banner: { label: 'Announcement Banner', icon: Bell, color: 'bg-blue-100 text-blue-700', desc: 'Top-of-page banner with a message and optional CTA' },
  popup: { label: 'Popup Modal', icon: MessageSquare, color: 'bg-purple-100 text-purple-700', desc: 'Modal that appears on page visit' },
  section: { label: 'Content Section', icon: Layout, color: 'bg-green-100 text-green-700', desc: 'Insertable content block on a page' },
};

export default function AdminPageExtrasPage() {
  const { api } = useAuth();
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    extra_type: 'banner', target_page: 'all', title: '', content: '', image_url: '',
    button_text: '', button_link: '', bg_color: '#1e3a5f', text_color: '#ffffff',
    position: 'top', start_date: '', end_date: '', active: true,
    layout: 'centered', image_position: 'top', image_style: 'cover',
    text_align: 'center', title_size: '3xl', section_padding: 'lg', border_style: 'none',
  });

  useEffect(() => { fetchExtras(); }, []);

  const fetchExtras = async () => {
    try { const r = await api.get('/page-extras/'); setExtras(r.data); }
    catch {} finally { setLoading(false); }
  };

  const openDialog = (extra = null) => {
    if (extra) {
      setEditing(extra);
      setForm({ ...extra, start_date: extra.start_date || '', end_date: extra.end_date || '' });
    } else {
      setEditing(null);
      setForm({ extra_type: 'banner', target_page: 'all', title: '', content: '', image_url: '',
        button_text: '', button_link: '', bg_color: '#1e3a5f', text_color: '#ffffff',
        position: 'top', start_date: '', end_date: '', active: true,
        layout: 'centered', image_position: 'top', image_style: 'cover',
        text_align: 'center', title_size: '3xl', section_padding: 'lg', border_style: 'none' });
    }
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/page-extras/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await api.post('/page-extras/', form);
        toast.success('Created');
      }
      setDialogOpen(false);
      fetchExtras();
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (extra) => {
    await api.patch(`/page-extras/${extra.id}`, { active: !extra.active });
    fetchExtras();
  };

  const deleteExtra = async (id) => {
    if (!window.confirm('Delete this page extra?')) return;
    await api.delete(`/page-extras/${id}/delete`);
    toast.success('Deleted');
    fetchExtras();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const banners = extras.filter(e => e.extra_type === 'banner');
  const popups = extras.filter(e => e.extra_type === 'popup');
  const sections = extras.filter(e => e.extra_type === 'section');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold">Page Extras</h2>
          <p className="text-sm text-muted-foreground">Add banners, popups, and content sections to any page — no code changes needed</p>
        </div>
        <Button onClick={() => openDialog()}><Plus className="h-4 w-4 mr-2" /> Add Extra</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(TYPE_CONFIG).map(([type, cfg]) => {
          const count = extras.filter(e => e.extra_type === type);
          const active = count.filter(e => e.active);
          return (
            <Card key={type}><CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${cfg.color}`}><cfg.icon className="h-5 w-5" /></div>
              <div>
                <p className="font-bold text-lg">{count.length}</p>
                <p className="text-xs text-muted-foreground">{cfg.label}s ({active.length} active)</p>
              </div>
            </CardContent></Card>
          );
        })}
      </div>

      {/* List */}
      {extras.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <Layout className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-semibold">No page extras yet</p>
          <p className="text-sm">Add a banner, popup, or section to enhance your pages</p>
          <Button className="mt-4" onClick={() => openDialog()}><Plus className="h-4 w-4 mr-2" /> Add Extra</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {extras.map(e => {
            const cfg = TYPE_CONFIG[e.extra_type] || TYPE_CONFIG.banner;
            const Icon = cfg.icon;
            const isExpired = e.end_date && new Date(e.end_date) < new Date();
            return (
              <Card key={e.id} className={`${!e.active || isExpired ? 'opacity-60' : ''}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${cfg.color}`}><Icon className="h-5 w-5" /></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{e.title}</h3>
                        <Badge className={cfg.color + ' text-xs'}>{cfg.label}</Badge>
                        <Badge variant="outline" className="text-xs">{PAGES.find(p => p.value === e.target_page)?.label || e.target_page}</Badge>
                        {!e.active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                        {isExpired && <Badge variant="outline" className="text-xs text-red-500">Expired</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">{e.content}</p>
                      {(e.start_date || e.end_date) && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {e.start_date || '∞'} → {e.end_date || '∞'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => toggleActive(e)} title={e.active ? 'Deactivate' : 'Activate'}>
                      {e.active ? <Eye className="h-4 w-4 text-green-500" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openDialog(e)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteExtra(e.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* CREATE/EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Page Extra' : 'Add Page Extra'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.extra_type} onValueChange={v => setForm(p => ({...p, extra_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{TYPE_CONFIG[form.extra_type]?.desc}</p>
              </div>
              <div className="space-y-2">
                <Label>Target Page</Label>
                <Select value={form.target_page} onValueChange={v => setForm(p => ({...p, target_page: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
                placeholder="e.g., Spring Registration Now Open!" />
            </div>

            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea value={form.content} onChange={e => setForm(p => ({...p, content: e.target.value}))}
                placeholder="Message body..." rows={3} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Button Text</Label>
                <Input value={form.button_text} onChange={e => setForm(p => ({...p, button_text: e.target.value}))}
                  placeholder="e.g., Register Now" />
              </div>
              <div className="space-y-2">
                <Label>Button Link</Label>
                <Input value={form.button_link} onChange={e => setForm(p => ({...p, button_link: e.target.value}))}
                  placeholder="e.g., /catalog" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Image URL (optional)</Label>
              <Input value={form.image_url} onChange={e => setForm(p => ({...p, image_url: e.target.value}))}
                placeholder="https://..." />
            </div>

            {/* Section & Popup visual options */}
            {(form.extra_type === 'section' || form.extra_type === 'popup') && (
              <>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Select value={form.position} onValueChange={v => setForm(p => ({...p, position: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top">Top of Page (after header)</SelectItem>
                      <SelectItem value="bottom">Bottom of Page (before footer)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Layout</Label>
                  <Select value={form.layout} onValueChange={v => setForm(p => ({...p, layout: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="centered">Centered</SelectItem>
                      <SelectItem value="image-left">Image Left, Text Right</SelectItem>
                      <SelectItem value="image-right">Image Right, Text Left</SelectItem>
                      <SelectItem value="full-width">Full Width Background</SelectItem>
                      <SelectItem value="card">Card Style</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Padding</Label>
                  <Select value={form.section_padding} onValueChange={v => setForm(p => ({...p, section_padding: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sm">Small</SelectItem>
                      <SelectItem value="md">Medium</SelectItem>
                      <SelectItem value="lg">Large</SelectItem>
                      <SelectItem value="xl">Extra Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Image Style</Label>
                  <Select value={form.image_style} onValueChange={v => setForm(p => ({...p, image_style: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cover">Cover (fill area)</SelectItem>
                      <SelectItem value="contain">Contain (fit inside)</SelectItem>
                      <SelectItem value="rounded">Rounded Corners</SelectItem>
                      <SelectItem value="circle">Circle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Text Align</Label>
                  <Select value={form.text_align} onValueChange={v => setForm(p => ({...p, text_align: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Title Size</Label>
                  <Select value={form.title_size} onValueChange={v => setForm(p => ({...p, title_size: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="xl">Small</SelectItem>
                      <SelectItem value="2xl">Medium</SelectItem>
                      <SelectItem value="3xl">Large</SelectItem>
                      <SelectItem value="4xl">Extra Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Border</Label>
                  <Select value={form.border_style} onValueChange={v => setForm(p => ({...p, border_style: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="subtle">Subtle</SelectItem>
                      <SelectItem value="accent">Accent Line</SelectItem>
                      <SelectItem value="shadow">Shadow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Background Color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={form.bg_color || '#f9fafb'} onChange={e => setForm(p => ({...p, bg_color: e.target.value}))} className="h-10 w-10 rounded cursor-pointer" />
                    <Input value={form.bg_color} onChange={e => setForm(p => ({...p, bg_color: e.target.value}))} placeholder="#f9fafb or transparent" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Text Color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={form.text_color || '#111827'} onChange={e => setForm(p => ({...p, text_color: e.target.value}))} className="h-10 w-10 rounded cursor-pointer" />
                    <Input value={form.text_color} onChange={e => setForm(p => ({...p, text_color: e.target.value}))} placeholder="#111827" />
                  </div>
                </div>
              </div>
              </>
            )}

            {form.extra_type === 'banner' && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Background Color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={form.bg_color || '#1e3a5f'} onChange={e => setForm(p => ({...p, bg_color: e.target.value}))} className="h-10 w-10 rounded cursor-pointer" />
                    <Input value={form.bg_color} onChange={e => setForm(p => ({...p, bg_color: e.target.value}))} className="flex-1" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Text Color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={form.text_color || '#ffffff'} onChange={e => setForm(p => ({...p, text_color: e.target.value}))} className="h-10 w-10 rounded cursor-pointer" />
                    <Input value={form.text_color} onChange={e => setForm(p => ({...p, text_color: e.target.value}))} className="flex-1" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Select value={form.position} onValueChange={v => setForm(p => ({...p, position: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top">Top of Page (after header)</SelectItem>
                      <SelectItem value="bottom">Bottom of Page (before footer)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date (optional)</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} />
              </div>
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm(p => ({...p, end_date: e.target.value}))} />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div><p className="font-medium text-sm">Active</p><p className="text-xs text-muted-foreground">Visible on the live site</p></div>
              <Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} />
            </div>

            {/* Preview */}
            {form.extra_type === 'banner' && form.title && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: form.bg_color || '#1e3a5f', color: form.text_color || '#fff' }}>
                  <span className="font-semibold">{form.title}</span>
                  {form.content && <span className="ml-2 opacity-90">{form.content.substring(0, 60)}</span>}
                  {form.button_text && <span className="ml-3 underline font-bold">{form.button_text}</span>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
